import { useEffect, useRef, useState, type CSSProperties, type FC } from 'react';
import { logger } from '../utils/logger';
import type {
  PlatformResponse,
  AdMeshTheme,
  DelegationActivationPayload,
  RecommendationReportPayload,
} from '../types/index';
import { AdMeshViewabilityTracker } from './AdMeshViewabilityTracker';

export interface AdMeshTailAdProps {
  recommendations: PlatformResponse[]; // Canonical platform responses
  theme?: AdMeshTheme;
  className?: string;
  style?: CSSProperties;
  sessionId?: string;
  triggerExposureUrl?: boolean;
  onDelegationConsent?: (recommendation: PlatformResponse) => boolean | Promise<boolean>;
  onDelegationActivated?: (payload: DelegationActivationPayload) => void | Promise<void>;
  onStartDelegation?: (recommendation: PlatformResponse) => void | Promise<void>;
  onPasteToInput?: (content: string) => void;
  onRecommendationReport?: (payload: RecommendationReportPayload) => void | Promise<void>;
}

// Utility function to validate and normalize URLs
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Helper function to get CTA label from backend
const getCTALabel = (ctaLabel?: string): string => {
  // Use provided CTA label from backend if available
  if (ctaLabel && ctaLabel.trim()) {
    return ctaLabel.trim();
  }

  // Return empty string if no CTA label provided (will hide CTA button)
  return '';
};

const getDisplayText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return '';
};

const asOptionalString = (value: unknown): string | undefined => {
  const text = getDisplayText(value);
  return text || undefined;
};

const truncateWords = (value: string, maxWords: number): string => {
  const words = value.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    return value;
  }
  return `${words.slice(0, maxWords).join(' ')}...`;
};

const formatProductPrice = (price: unknown, currency?: string): string => {
  if (price === null || price === undefined || price === '') {
    return '';
  }

  if (typeof price === 'number') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2,
      }).format(price);
    } catch {
      return String(price);
    }
  }

  return getDisplayText(price);
};

const formatProductDiscount = (
  discount: unknown,
  discountType?: string,
  currency?: string,
): string => {
  if (discount === null || discount === undefined || discount === '') {
    return '';
  }

  const normalizedType = (discountType || 'percentage').trim().toLowerCase();

  if (typeof discount === 'number') {
    if (normalizedType === 'amount' || normalizedType === 'fixed' || normalizedType === 'fixed_amount') {
      try {
        const formattedAmount = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'USD',
          maximumFractionDigits: 2,
        }).format(discount);
        return `${formattedAmount} off`;
      } catch {
        return `${discount} off`;
      }
    }

    return `${discount}% off`;
  }

  const text = getDisplayText(discount);
  if (!text) {
    return '';
  }
  if (/%|off/i.test(text)) {
    return text;
  }
  if (normalizedType === 'amount' || normalizedType === 'fixed' || normalizedType === 'fixed_amount') {
    return `${text} off`;
  }
  return `${text}% off`;
};

const TAIL_AD_COLORS = {
  surface: '#ffffff',
  surfaceMuted: '#f9fafb',
  surfaceSubtle: '#f8fafc',
  border: 'rgba(17, 24, 39, 0.10)',
  text: '#111827',
  textSecondary: '#4b5563',
  textMuted: '#6b7280',
  link: '#2563eb',
  logoFallbackBg: '#f3f4f6',
  logoFallbackText: '#4b5563',
  productImageFallbackBg: '#e5e7eb',
  successText: '#065f46',
  successBg: '#d1fae5',
};

export const AdMeshTailAd: FC<AdMeshTailAdProps> = ({
  recommendations,
  theme,
  className = '',
  style = {},
  sessionId,
  triggerExposureUrl = true,
  onDelegationConsent,
  onDelegationActivated,
  onStartDelegation,
  onPasteToInput,
}) => {
  // Validate inputs - return null if empty
  if (!recommendations || recommendations.length === 0) {
    logger.log('[AdMesh Tail Ad] No recommendations provided - not rendering');
    return null;
  }

  // Get the first recommendation's data for CTA and tracking
  const firstRecommendation = recommendations[0];
  const productId = firstRecommendation?.recommendation?.offerId || '';
  const exposureUrl = firstRecommendation?.tracking?.exposure_url || '';
  const recommendationId = firstRecommendation?.recommendation_id || '';
  const shortDescription = firstRecommendation?.recommendation?.creative?.description || '';
  const disclosure = firstRecommendation?.recommendation?.disclosure || 'Sponsored';
  const brandName = firstRecommendation?.recommendation?.creative?.brand_name || '';
  const productName =
    firstRecommendation?.recommendation?.creative?.headline ||
    firstRecommendation?.recommendation?.creative?.brand_name ||
    '';
  const logoUrl = firstRecommendation?.recommendation?.creative?.logo_url || '';
  const clickUrl =
    firstRecommendation?.tracking?.click_url ||
    firstRecommendation?.recommendation?.creative?.landing_page_url ||
    '';
  const ctaLabel = getCTALabel(firstRecommendation?.recommendation?.creative?.cta_text);
  const relatedProducts = firstRecommendation?.recommendation?.creative?.products || [];
  const hasProductRail = relatedProducts.length > 1;
  const visibleProducts = hasProductRail ? relatedProducts.slice(0, 4) : [];
  const recommendationFormat = firstRecommendation?.recommendation?.format || 'tail';
  const isBridgeRecommendation = recommendationFormat === 'bridge';
  const delegationBrandName = firstRecommendation?.delegation?.brand_name || brandName;
  const hasDelegation = firstRecommendation?.delegation?.available === true;
  const shouldShowDelegationButton = isBridgeRecommendation && hasDelegation;
  const delegationCtaText = 'Talk to Agent';
  const shouldShowPrimaryCta = !hasProductRail && !!ctaLabel && !!clickUrl;

  // For tail format, we need at least brand name
  // But we still validate that we have at least one recommendation with data
  if (!brandName && !productName && !shortDescription) {
    logger.log('[AdMesh Tail Ad] No valid recommendation data provided - not rendering', {
      reason: 'brand, headline, and description are missing',
      brandName,
      hasFirstRecommendation: !!firstRecommendation,
      recommendationId,
      recommendationKeys: firstRecommendation ? Object.keys(firstRecommendation) : []
    });
    return null;
  }
  
  // Build headline parts with priority:
  // 1. If offer_summary exists: "Brand — Offer Summary"
  // 2. If product_name exists: "Brand — Product Name"
  // 3. Otherwise: just "Brand"
  let headlineText = brandName;
  let headlineSuffix = '';
  
  if (productName && productName !== brandName) {
    headlineSuffix = productName;
  }
  
  if (headlineSuffix) {
    headlineText = `${brandName} — ${headlineSuffix}`;
  }

  logger.debug('[AdMeshTailAd] 📊 Rendering with tracking data:', {
    recommendationId,
    productId,
    exposureUrl: exposureUrl ? 'present' : 'MISSING',
    sessionId: sessionId ? 'present' : 'MISSING',
    recommendationsCount: recommendations.length,
    clickUrl: clickUrl ? clickUrl : 'MISSING',
    clickUrlSource: firstRecommendation?.tracking?.click_url ? 'tracking.click_url' :
                    firstRecommendation?.recommendation?.creative?.landing_page_url ? 'recommendation.creative.landing_page_url' :
                    'none',
    shortDescription: shortDescription ? 'present' : 'MISSING',
    brandName,
    productName,
    headlineText,
    triggerExposureUrl
  });
  logger.log('[AdMeshTailAd] ✅ Rendering tail ad', {
    recommendationId,
    brandName,
    productName,
    hasDescription: !!shortDescription,
    hasClickUrl: !!clickUrl,
    hasLogoUrl: !!logoUrl,
    recommendationFormat,
  });

  // Handler for tracking clicks on the entire tail ad
  const handleContainerClick = (source: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    logger.log(`AdMesh tail ad ${source} clicked`);
    if (typeof window !== 'undefined' && (window as any).admeshTracker) {
      (window as any).admeshTracker.trackClick({
        recommendationId,
        productId,
        clickUrl: clickUrl,
        source: source
      }).catch(() => {
        logger.error(`[AdMesh] Failed to track ${source} click`);
      });
    }
  };

  // Handler for brand name link click
  const handleBrandNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleContainerClick('tail_ad_brand_name');
  };

  // Handler for CTA link click
  const handleCTAClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleContainerClick('tail_ad_cta');
  };

  // Handler for logo click
  const handleLogoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleContainerClick('tail_ad_logo');
  };

  const handleCardClick = () => {
    if (hasProductRail || !clickUrl) {
      return;
    }
    handleContainerClick('tail_ad_card');
    if (typeof window !== 'undefined') {
      window.open(clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (hasProductRail || !clickUrl) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  const requestDefaultConsent = (): boolean => {
    return true;
  };

  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const [isConsentConnecting, setIsConsentConnecting] = useState(false);
  const consentResolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (consentResolverRef.current) {
        consentResolverRef.current(false);
        consentResolverRef.current = null;
      }
    };
  }, []);

  const resolveConsentPrompt = (value: boolean) => {
    if (value) {
      setIsConsentConnecting(true);
    }
    consentResolverRef.current?.(value);
    consentResolverRef.current = null;
    if (!value) {
      setIsConsentConnecting(false);
      setShowConsentPrompt(false);
    }
  };

  const requestConsent = async (): Promise<boolean> => {
    if (onDelegationConsent) {
      return await onDelegationConsent(firstRecommendation);
    }
    if (!requestDefaultConsent()) {
      return false;
    }
    return await new Promise<boolean>((resolve) => {
      consentResolverRef.current = resolve;
      setShowConsentPrompt(true);
    });
  };

  const trackDelegationEngagement = async () => {
    if (!(recommendationId && sessionId)) {
      return;
    }

    try {
      const apiBaseUrl =
        (typeof window !== 'undefined' && (window as any).__ADMESH_API_BASE_URL__) ||
        'https://api.useadmesh.com';

      const response = await fetch(`${apiBaseUrl}/click/bridge-engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recommendation_id: recommendationId,
          session_id: sessionId,
          is_test: false,
        }),
      });

      if (!response.ok) {
        logger.warn('[AdMesh Tail Ad] Delegation engagement tracking failed:', response.statusText);
      }
    } catch (error) {
      logger.error('[AdMesh Tail Ad] Error tracking delegation engagement:', error);
    }
  };

  const handleDelegationClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!firstRecommendation || !shouldShowDelegationButton) {
      return;
    }

    handleContainerClick('tail_ad_delegation_cta');

    const consentGranted = await requestConsent();
    if (!consentGranted) {
      logger.log('[AdMesh Tail Ad] Delegation consent declined');
      return;
    }

    void trackDelegationEngagement();

    const delegation = firstRecommendation.delegation;
    const delegationPayload: DelegationActivationPayload | null =
      delegation?.available
        ? {
            recommendation_id: firstRecommendation.recommendation_id,
            brand_id: asOptionalString(delegation.brand_id),
            brand_name: asOptionalString(
              delegation.brand_name || firstRecommendation.recommendation?.creative?.brand_name,
            ),
            mcp_endpoint: asOptionalString(delegation.mcp_endpoint),
            eligibility_reason: asOptionalString(delegation.eligibility_reason),
            delegate_reason: asOptionalString(delegation.delegate_reason),
            session_goal: asOptionalString(delegation.session_goal),
            desired_outcome_type: delegation.desired_outcome_type,
            required_user_inputs: Array.isArray(delegation.required_user_inputs)
              ? delegation.required_user_inputs.filter((value): value is string => typeof value === 'string')
              : [],
            required_consent_types: Array.isArray(delegation.required_consent_types)
              ? delegation.required_consent_types.filter((value): value is string => typeof value === 'string')
              : [],
            success_outcome: asOptionalString(delegation.success_outcome),
            fallback_message: asOptionalString(delegation.fallback_message),
            session_mode: asOptionalString(delegation.session_mode),
            status: asOptionalString(delegation.status),
            capabilities: delegation.capabilities || [],
            allowed_data_types: delegation.allowed_data_types || [],
            consent_requirements: delegation.consent_requirements || [],
            auth_model: asOptionalString(delegation.auth_model),
            handoff_protocol:
              delegation.handoff_protocol && typeof delegation.handoff_protocol === 'object'
                ? delegation.handoff_protocol
                : undefined,
            supported_actions: delegation.supported_actions || [],
            metadata:
              delegation.metadata && typeof delegation.metadata === 'object'
                ? delegation.metadata
                : undefined,
            platform_response: firstRecommendation,
          }
        : null;

    if (onDelegationActivated && delegationPayload) {
      try {
        await onDelegationActivated(delegationPayload);
      } finally {
        setIsConsentConnecting(false);
        setShowConsentPrompt(false);
      }
      return;
    }

    if (onStartDelegation) {
      try {
        await onStartDelegation(firstRecommendation);
      } finally {
        setIsConsentConnecting(false);
        setShowConsentPrompt(false);
      }
      return;
    }

    const fallbackPrompt = firstRecommendation.recommendation?.creative?.follow_up_suggestion || '';
    if (fallbackPrompt && onPasteToInput) {
      onPasteToInput(fallbackPrompt);
      setIsConsentConnecting(false);
      setShowConsentPrompt(false);
      return;
    }

    if (fallbackPrompt && typeof window !== 'undefined' && (window as any).__admesh_setMessage) {
      (window as any).__admesh_setMessage(fallbackPrompt);
      setIsConsentConnecting(false);
      setShowConsentPrompt(false);
      return;
    }

    setIsConsentConnecting(false);
    setShowConsentPrompt(false);
  };

  // State for logo load error
  const [logoError, setLogoError] = useState(false);

  // Get first letter of brand name for fallback
  const brandInitial = brandName ? brandName.charAt(0).toUpperCase() : 'B';

  // Tail ads use their own neutral palette so host light/dark themes cannot
  // make sponsored content unreadable.
  const cardBackground = TAIL_AD_COLORS.surface;
  const cardBorder = TAIL_AD_COLORS.border;
  const cardBorderRadius = theme?.borderRadius || theme?.components?.card?.borderRadius || '8px';
  
  // Shadow styles - definitive shadow for floating feel
  const defaultShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
  const hoverShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
  
  const cardShadow = theme?.shadows?.medium || theme?.components?.card?.boxShadow || defaultShadow;
  const cardHoverShadow = theme?.shadows?.large || hoverShadow;

  return (
    <AdMeshViewabilityTracker
      productId={productId}
      recommendationId={recommendationId}
      exposureUrl={exposureUrl}
      sessionId={sessionId}
      triggerExposureUrl={triggerExposureUrl}
      className={`admesh-tail-ad ${className}`}
      style={{
        fontFamily: theme?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: 1.4,
        ...style
      }}
    >
      <style>{`
        .admesh-tail-product-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .admesh-tail-product-scroll::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
      `}</style>
      <div 
        className="tail-ad-container flex flex-col gap-3 sm:flex-row"
        role={!hasProductRail && clickUrl ? 'link' : undefined}
        tabIndex={!hasProductRail && clickUrl ? 0 : undefined}
        style={{
          ...theme?.components?.card,
          backgroundColor: cardBackground,
          borderRadius: cardBorderRadius,
          padding: '16px',
          boxShadow: cardShadow,
          border: `1px solid ${cardBorder}`,
          color: TAIL_AD_COLORS.text,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateY(0)',
          cursor: !hasProductRail && clickUrl ? 'pointer' : undefined,
        }}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = cardHoverShadow;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = cardShadow;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {/* Left Section: Logo Area (10% width, stacks on mobile) */}
        {logoUrl && (
          <div 
            className="flex-shrink-0 flex items-center justify-start pr-0 sm:justify-center sm:pr-2"
            style={{
              width: 'auto',
              minWidth: '48px'
            }}
          >
            {!logoError && isValidUrl(logoUrl) ? (
              <a
                href={clickUrl || '#'}
                target={clickUrl ? "_blank" : undefined}
                rel={clickUrl ? "noopener noreferrer" : undefined}
                onClick={clickUrl ? handleLogoClick : undefined}
                className="block"
                style={{ 
                  cursor: clickUrl ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: '64px'
                }}
              >
                <img
                  src={logoUrl}
                  alt={`${brandName} logo`}
                  className="object-cover"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '64px',
                    maxHeight: '64px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                  onError={() => {
                    setLogoError(true);
                    logger.debug('[AdMesh Tail Ad] Logo failed to load, showing fallback');
                  }}
                />
              </a>
            ) : (
              <div
                className="flex items-center justify-center text-lg font-semibold"
                style={{
                  width: '100%',
                  maxWidth: '64px',
                  aspectRatio: '1',
                  borderRadius: '8px',
                  minHeight: '48px',
                  backgroundColor: TAIL_AD_COLORS.logoFallbackBg,
                  color: TAIL_AD_COLORS.logoFallbackText,
                  fontSize: '18px',
                }}
              >
                {brandInitial}
              </div>
            )}
          </div>
        )}

        {/* Right Section: Content Area (90% width) */}
        <div 
          className="flex-1"
          style={{
            width: '100%',
            minWidth: 0 // Allow flex item to shrink below content size
          }}
        >
          {/* Headline: Brand Name (clickable link) — Offer Summary / Product Name / Brand only */}
          {headlineText && !hasProductRail && (
            <div className="mb-2">
              <h3 className="font-semibold text-base" style={{ fontSize: '14px', lineHeight: 1.35, color: TAIL_AD_COLORS.text }}>
                {clickUrl && brandName ? (
                  <>
                    <a
                      href={clickUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline transition-colors duration-200 font-semibold"
                      style={{
                        color: TAIL_AD_COLORS.link,
                        textDecoration: 'underline',
                        textDecorationColor: TAIL_AD_COLORS.link,
                        textUnderlineOffset: '2px'
                      }}
                      onClick={handleBrandNameClick}
                    >
                      {brandName}
                    </a>
                    {headlineSuffix && ` — ${headlineSuffix}`}
                  </>
                ) : (
                  headlineText
                )}
              </h3>
            </div>
          )}
          
          {/* Description: Full short_description */}
          {shortDescription && !hasProductRail && (
            <p className="text-sm mb-3 leading-relaxed" style={{ fontSize: '13px', lineHeight: 1.45, color: TAIL_AD_COLORS.textSecondary }}>
              {shortDescription}
            </p>
          )}

          {hasProductRail && visibleProducts.length > 0 && (
            <div className="mb-3">
              <div
                className="admesh-tail-product-scroll"
                style={{
                  marginBottom: '8px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehaviorX: 'contain',
                  overscrollBehaviorY: 'none',
                  scrollBehavior: 'smooth',
                  paddingBottom: '2px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '10px',
                    minWidth: 'max-content',
                    scrollSnapType: 'x proximity',
                    paddingRight: 0,
                  }}
                >
                  {visibleProducts.map((product) => {
                const productName = getDisplayText(product.product_name) || 'Product';
                const productLink =
                  getDisplayText(product.product_link) ||
                  getDisplayText(product.product_url) ||
                  clickUrl ||
                  '#';
                const productDescription = truncateWords(getDisplayText(product.product_description), 10);
                const productPrice = formatProductPrice(product.product_price, product.product_currency);
                const productDiscount = formatProductDiscount(
                  product.product_discount,
                  product.product_discount_type,
                  product.product_currency,
                );
                const productImageUrl = getDisplayText(product.product_image_url);
                const fallbackLogoUrl = logoUrl && isValidUrl(logoUrl) ? logoUrl : '';
                const displayProductImageUrl =
                  productImageUrl && isValidUrl(productImageUrl)
                    ? productImageUrl
                    : fallbackLogoUrl;
                const productCtaLabel = getCTALabel(product.product_cta_label);
                const hasProductLink = productLink !== '#';

                return (
                  <a
                    key={product.product_id || `${recommendationId}-${productName}`}
                    href={productLink}
                    target={hasProductLink ? '_blank' : undefined}
                    rel={hasProductLink ? 'noopener noreferrer' : undefined}
                    onClick={(e) => handleContainerClick(`tail_ad_product_${product.product_id || 'item'}`, e)}
                    className="block"
                    style={{
                      border: `1px solid ${cardBorder}`,
                      borderRadius: '8px',
                      padding: '10px',
                      backgroundColor: TAIL_AD_COLORS.surfaceMuted,
                      textDecoration: 'none',
                      color: TAIL_AD_COLORS.text,
                      minWidth: 'min(264px, 82vw)',
                      width: 'clamp(220px, 72vw, 312px)',
                      flex: '0 0 clamp(220px, 72vw, 312px)',
                      scrollSnapAlign: 'start',
                    }}
                  >
                    <div className="flex items-start gap-2">
                      {displayProductImageUrl ? (
                        <img
                          src={displayProductImageUrl}
                          alt={productName}
                          style={{
                            width: '44px',
                            height: '44px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          className="flex items-center justify-center text-xs font-semibold"
                          style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '6px',
                            flexShrink: 0,
                            backgroundColor: TAIL_AD_COLORS.productImageFallbackBg,
                            color: TAIL_AD_COLORS.textSecondary,
                            fontSize: '12px',
                          }}
                        >
                          {productName.charAt(0).toUpperCase()}
                        </div>
                      )}

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p
                          className="font-medium text-sm"
                          style={{
                            fontSize: '13px',
                            lineHeight: 1.3,
                            color: TAIL_AD_COLORS.text,
                            marginBottom: '4px',
                          }}
                        >
                          {productName}
                        </p>

                        {productDescription && (
                          <p
                            className="text-xs"
                            style={{
                              fontSize: '12px',
                              lineHeight: 1.3,
                              color: TAIL_AD_COLORS.textSecondary,
                              marginBottom: '6px',
                            }}
                          >
                            {productDescription}
                          </p>
                        )}

                        {(productPrice || productDiscount) && (
                          <div
                            className="flex items-center gap-2 flex-wrap"
                            style={{
                              marginBottom: productCtaLabel ? '6px' : 0,
                            }}
                          >
                            {productPrice && (
                              <div className="flex items-center gap-1">
                                <span
                                  style={{
                                    fontSize: '10px',
                                    color: TAIL_AD_COLORS.textMuted,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                  }}
                                >
                                  Price
                                </span>
                                <span
                                  className="font-semibold"
                                  style={{
                                    fontSize: '13px',
                                    color: TAIL_AD_COLORS.text,
                                  }}
                                >
                                  {productPrice}
                                </span>
                              </div>
                            )}
                            {productDiscount && (
                              <span
                                className="text-xs"
                                style={{
                                  fontSize: '11px',
                                  color: TAIL_AD_COLORS.successText,
                                  backgroundColor: TAIL_AD_COLORS.successBg,
                                  borderRadius: '999px',
                                  padding: '2px 8px',
                                  fontWeight: 600,
                                }}
                              >
                                {productDiscount}
                              </span>
                            )}
                          </div>
                        )}

                        {productCtaLabel && (
                          <span
                            className="font-medium"
                            style={{
                              fontSize: '12px',
                              color: TAIL_AD_COLORS.link,
                              textDecoration: 'underline',
                              textUnderlineOffset: '2px',
                            }}
                          >
                            {productCtaLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  </a>
                );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* CTA Link, Feedback Buttons, and Sponsored Label */}
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {hasProductRail && brandName && (
                <span
                  className="text-sm"
                  style={{
                    fontSize: '13px',
                    color: TAIL_AD_COLORS.textSecondary,
                    fontWeight: 400,
                  }}
                >
                  {brandName}
                </span>
              )}

              {shouldShowDelegationButton ? (
                <button
                  type="button"
                  onClick={handleDelegationClick}
                  className="underline transition-colors duration-200 font-medium text-sm"
                  style={{
                    color: TAIL_AD_COLORS.link,
                    textDecoration: 'underline',
                    textDecorationColor: TAIL_AD_COLORS.link,
                    textUnderlineOffset: '2px',
                    fontSize: '13px',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  {delegationCtaText}
                </button>
              ) : null}

              {shouldShowPrimaryCta && (
                <a
                  href={clickUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline transition-colors duration-200 font-medium text-sm"
                  style={{
                    color: TAIL_AD_COLORS.link,
                    textDecoration: 'underline',
                    textDecorationColor: TAIL_AD_COLORS.link,
                    textUnderlineOffset: '2px',
                    fontSize: '13px',
                  }}
                  onClick={handleCTAClick}
                >
                  {ctaLabel}
                </a>
              )}

              {hasProductRail && relatedProducts.length > visibleProducts.length && (
                <span
                  className="text-xs"
                  style={{ fontSize: '11px', color: TAIL_AD_COLORS.textMuted }}
                >
                  +{relatedProducts.length - visibleProducts.length} more
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <p className="text-xs" style={{ fontSize: '11px', color: TAIL_AD_COLORS.textMuted }}>
                {disclosure || 'Sponsored'}
              </p>
            </div>
          </div>

          {showConsentPrompt ? (
            <div
              style={{
                marginTop: '10px',
                border: `1px solid ${cardBorder}`,
                borderRadius: '12px',
                backgroundColor: TAIL_AD_COLORS.surfaceSubtle,
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: TAIL_AD_COLORS.link,
                  }}
                >
                  Confirm Agent Access
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.4,
                    color: TAIL_AD_COLORS.text,
                    fontWeight: 600,
                  }}
                >
                  Allow {delegationBrandName || 'this brand'} to start a delegated session?
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    lineHeight: 1.45,
                    color: TAIL_AD_COLORS.textSecondary,
                  }}
                >
                  The agent will only use the details you choose to share for this request.
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={isConsentConnecting}
                  onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isConsentConnecting) {
                    return;
                  }
                  resolveConsentPrompt(false);
                }}
                  style={{
                    borderRadius: '10px',
                    border: `1px solid ${cardBorder}`,
                    background: cardBackground,
                    color: TAIL_AD_COLORS.text,
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '8px 12px',
                    cursor: isConsentConnecting ? 'not-allowed' : 'pointer',
                    opacity: isConsentConnecting ? 0.65 : 1,
                  }}
                >
                  Not now
                </button>
                <button
                  type="button"
                  disabled={isConsentConnecting}
                  onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isConsentConnecting) {
                    return;
                  }
                  resolveConsentPrompt(true);
                }}
                  style={{
                    borderRadius: '10px',
                    border: 'none',
                    background: '#2563eb',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '8px 12px',
                    cursor: isConsentConnecting ? 'wait' : 'pointer',
                    opacity: isConsentConnecting ? 0.86 : 1,
                  }}
                >
                  {isConsentConnecting ? 'Connecting...' : 'Allow agent'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </AdMeshViewabilityTracker>
  );
};

export default AdMeshTailAd;
